import type { ReactNode } from 'react';
import { createStore } from './store';
import { navigate, routeStore, type RecordType } from './router';

// Everything that floats above the current view: the search palette, the notifications panel and dialogs.
// Record sheets and the admin console live in the address bar instead (see router.ts).

export interface DialogSpec {
  /** Read out by screen readers when the dialog opens. */
  label: string;
  /** Width: "wide" (820px) or "mid" (680px); the default is 560px. */
  size?: 'wide' | 'mid';
  render: (close: () => void) => ReactNode;
  /** Runs after the dialog closes, however it was closed. */
  onClose?: () => void;
}

interface OverlayState {
  palette: { open: boolean; query: string; seq: number };
  notifications: boolean;
  dialog: (DialogSpec & { key: number }) | null;
}

export const overlayStore = createStore<OverlayState>({
  palette: { open: false, query: '', seq: 0 },
  notifications: false,
  dialog: null,
});

let dialogKey = 1;

export function openPalette(query = ''): void {
  const s = overlayStore.get();
  overlayStore.set({ palette: { open: true, query, seq: s.palette.seq + 1 }, notifications: false, dialog: null });
}

export function closePalette(): void {
  const s = overlayStore.get();
  if (s.palette.open) overlayStore.set({ palette: { ...s.palette, open: false } });
}

export function togglePalette(): void {
  if (overlayStore.get().palette.open) closePalette();
  else openPalette('');
}

export function openNotifications(): void {
  closePalette();
  overlayStore.set({ notifications: true });
}

export function closeNotifications(): void {
  overlayStore.set({ notifications: false });
}

export function toggleNotifications(): void {
  if (overlayStore.get().notifications) closeNotifications();
  else openNotifications();
}

/** Opens a dialog, replacing any open one. Returns a function that closes it. */
export function openDialog(spec: DialogSpec): () => void {
  const key = dialogKey++;
  const previous = overlayStore.get().dialog;
  const palette = overlayStore.get().palette;
  overlayStore.set({ dialog: { ...spec, key }, palette: { ...palette, open: false } });
  previous?.onClose?.();
  return () => closeDialog(key);
}

/** Closes the open dialog, or only the given one when a key is passed. */
export function closeDialog(key?: number): void {
  const current = overlayStore.get().dialog;
  if (!current || (key !== undefined && current.key !== key)) return;
  overlayStore.set({ dialog: null });
  current.onClose?.();
}

export function openRecord(type: RecordType, id: string, opts: { tab?: string; focus?: string } = {}): void {
  closePalette();
  closeNotifications();
  navigate({ record: { type, id, tab: opts.tab, focus: opts.focus } });
}

/** Switches the tab of the open record without adding a history entry. */
export function setRecordTab(tab: string): void {
  const record = routeStore.get().route.record;
  if (record) navigate({ record: { ...record, tab, focus: undefined } }, { replace: true });
}

export function openAdmin(section = 'team'): void {
  closePalette();
  closeNotifications();
  navigate({ admin: section });
}

export function closeSheet(): void {
  const route = routeStore.get().route;
  if (route.record || route.admin) navigate({ record: null, admin: null });
}

export function anyLayerOpen(): boolean {
  const s = overlayStore.get();
  return s.palette.open || !!s.dialog || composerOpen();
}

// The composer is owned by the mail area; it reports whether it is open so keys and focus can respect it.
let composerIsOpen = false;
export function setComposerOpen(open: boolean): void {
  composerIsOpen = open;
}
export function composerOpen(): boolean {
  return composerIsOpen;
}
